import { GlobalAudioNotificationService } from '@host/services/audio-notification.service';
import { Logger } from './logger.service';

/**
 * Window within which two sounds sharing a `dedupeKey` collapse into one.
 *
 * Some real-world moments emit two independent socket events. Someone walking into your
 * office produces both `user.room.changed` (OfficeEventRouter plays the knock) and
 * `vo.notification.created` (GlobalNotificationService plays the room alert), so the user
 * heard two sounds for one arrival. Rather than arbitrarily picking a winner and changing the
 * characteristic knock UX, both pass the same key and the pair collapses.
 */
const DEDUPE_WINDOW_MS = 1500;

const lastPlayedAt = new Map<string, number>();

/**
 * Shared by OfficeEventRouter's knock and GlobalNotificationService's room alert. One person
 * arriving emits `user.room.changed` AND `vo.notification.created`, and both would otherwise
 * sound. Exported so the two cannot drift onto different keys and silently start doubling.
 */
export const ROOM_ARRIVAL_SOUND_KEY = 'room-arrival';

interface PlayOptions {
  /**
   * Opt in to collapsing. Only pass this where two events genuinely describe one moment, and
   * use the same key at both call sites.
   *
   * Deliberately NOT defaulted to the sound name: chat messages all share one sound, so
   * keying on it would swallow the second of two messages arriving in quick succession. Chat
   * needs no collapsing anyway — RocketChatDDPService already dedupes by message id.
   */
  dedupeKey?: string;
}

/**
 * Play a notification sound, gated on the caller's already-resolved `app` channel.
 *
 * Every sound about *other people* should go through here. Mute and volume are not
 * re-checked: they live in AudioNotificationService, which displayPrefs.store keeps in sync,
 * so they apply uniformly instead of only to chat as they did before NGE-988.
 *
 * Self-action feedback (your own door lock/unlock) deliberately does NOT come through here —
 * it is UI feedback for something you just did, not a notification about someone else, so it
 * should not be silenced by turning off Room Notifications. It still honours mute and volume.
 */
export function playNotificationSound(
  name: string,
  appChannelEnabled: boolean,
  options: PlayOptions = {}
): void {
  if (!appChannelEnabled) {
    Logger.debug(`[NotificationSound] "${name}" suppressed — app channel disabled`);
    return;
  }

  const { dedupeKey } = options;
  if (dedupeKey) {
    const now = Date.now();
    const previous = lastPlayedAt.get(dedupeKey);
    if (previous !== undefined && now - previous < DEDUPE_WINDOW_MS) {
      Logger.debug(
        `[NotificationSound] "${name}" collapsed on key "${dedupeKey}" — played ${now - previous}ms ago`
      );
      return;
    }
    lastPlayedAt.set(dedupeKey, now);
  }

  GlobalAudioNotificationService.play(name);
}
